import { HugeiconsIcon } from "@hugeicons/react";
import { Call02Icon, Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Avatar } from "shared/components/Avatar";
import { cx } from "shared/utils";
import { formatPhoneDisplay } from "../views/phone-numbers/lib/format";
import { useSoftphone } from "./SoftphoneProvider";
import { SOFTPHONE_OVERLAY_Z_CLASS } from "./stacking";

const ACTIVE_AVATAR_COLOR = "#29B6F6";

/**
 * Incoming-call popup (CRM-905, Figma 23471:85906). Floats bottom-right like
 * the in-call card: caller avatar + number, matched lead name, "Incoming call"
 * and the Start call / Cancel pair. ("Send to AI" from the mockup is out of
 * scope — no AI handoff in this ticket.) The X collapses to the banner.
 */
export function IncomingCallScreen({ onMinimize }: { onMinimize: () => void }) {
  const { incomingCall, acceptIncoming, rejectIncoming } = useSoftphone();

  if (!incomingCall) return null;

  const displayNumber = formatPhoneDisplay(incomingCall.fromNumber);
  const displayName = incomingCall.leadName?.trim();

  return (
    <div
      role="dialog"
      aria-label="Incoming call"
      className={cx(
        "fixed bottom-4 right-4 w-[360px] max-w-[calc(100vw-2rem)] rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-alpha-100",
        SOFTPHONE_OVERLAY_Z_CLASS
      )}
    >
      <button
        type="button"
        aria-label="Minimize"
        onClick={onMinimize}
        className="absolute right-3 top-3 grid size-8 place-items-center rounded-full text-content-300 transition hover:bg-alpha-40 hover:text-content-1000"
      >
        <HugeiconsIcon icon={Cancel01Icon} size={20} />
      </button>

      <div className="flex flex-col items-center text-center">
        <Avatar
          name={displayName || displayNumber}
          size={72}
          className="text-2xl font-semibold"
          fallbackBackgroundColor={ACTIVE_AVATAR_COLOR}
        />

        <p className="mt-4 text-base font-semibold text-content-1000">
          {displayNumber}
        </p>
        {displayName ? (
          <p className="mt-0.5 text-sm text-content-400">{displayName}</p>
        ) : null}
        {incomingCall.numberLabel ? (
          <p className="mt-0.5 text-xs text-content-300">
            To: {incomingCall.numberLabel}
          </p>
        ) : null}

        <div className="mt-8 min-h-12">
          <p className="text-lg font-semibold text-content-1000">
            Incoming call
          </p>
        </div>

        <div className="mt-6 flex items-start justify-center gap-10">
          <IncomingControlButton
            label="Start call"
            onClick={acceptIncoming}
            circleClassName="bg-success-500 text-white hover:bg-success-600"
            icon={Call02Icon}
          />
          <IncomingControlButton
            label="Cancel"
            onClick={rejectIncoming}
            circleClassName="bg-danger-500 text-white hover:bg-danger-600"
            icon={Cancel01Icon}
          />
        </div>
      </div>
    </div>
  );
}

function IncomingControlButton({
  label,
  onClick,
  circleClassName,
  icon
}: {
  label: string;
  onClick: () => void;
  circleClassName: string;
  icon: typeof Call02Icon;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-2"
    >
      <span
        className={cx(
          "grid size-12 place-items-center rounded-full transition",
          circleClassName
        )}
      >
        <HugeiconsIcon icon={icon} size={22} />
      </span>
      <span className="text-xs text-content-400">{label}</span>
    </button>
  );
}
